import { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { getProducts, getCategories } from '@/lib/api';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { SlidersHorizontal, Grid3X3, Grid2X2, Search, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { getImageUrl } from '@/components/ImageUploadField';

// Optimise les URLs : resize MongoDB via ?w=, Unsplash via params URL
const optimizeImg = (url, width = 600) => {
  if (!url) return url;
  if (url.startsWith('http') && url.includes('/api/images/')) {
    // URL complète MongoDB déjà construite par getImageUrl — ajouter ?w=
    return url.includes('?') ? url : `${url}?w=${width}`;
  }
  if (url.includes('images.unsplash.com')) {
    const base = url.replace(/&w=\d+/, '').replace(/&q=\d+/, '');
    return `${base}&w=${width}&q=75`;
  }
  return url;
};

export default function CataloguePage() {
  const { categorySlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [sortBy, setSortBy] = useState('newest');
  const [gridCols, setGridCols] = useState(3);
  const [searchQuery, setSearchQuery] = useState('');
  const [imagesLoaded, setImagesLoaded] = useState({});
  const [imagesError, setImagesError] = useState({});
  const [visibleCount, setVisibleCount] = useState(24);
  const [allProducts, setAllProducts] = useState([]);

  const PAGE_SIZE = 24;

  // Fetch réseau : dépend uniquement de la catégorie.
  // Le tri et la recherche sont calculés côté client sur la liste en mémoire —
  // sans ça, chaque frappe déclenchait un aller-retour + un flash sur 24 images.
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setImagesLoaded({});
      setImagesError({});
      setVisibleCount(24);
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          getProducts(selectedCategory ? { category_id: selectedCategory } : {}),
          getCategories()
        ]);
        setAllProducts(productsRes.data || []);
        setCategories(categoriesRes.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedCategory]);

  // Filtrage + tri côté client — instantané, aucune requête réseau.
  useEffect(() => {
    let list = [...allProducts];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q) ||
        p.material?.toLowerCase().includes(q)
      );
    }
    switch (sortBy) {
      case 'price-asc':
        list.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        list.sort((a, b) => b.price - a.price);
        break;
      case 'name':
        list.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
    setProducts(list);
    setVisibleCount(24);
  }, [allProducts, searchQuery, sortBy]);

  const clearSearch = () => setSearchQuery('');

  const handleCategoryChange = (value) => {
    setSelectedCategory(value === 'all' ? '' : value);
    if (value === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', value);
    }
    setSearchParams(searchParams);
  };

  const FilterSidebar = ({ mobile = false }) => (
    <div className={mobile ? '' : 'sticky top-28'}>
      <h3 className="font-heading text-lg font-semibold mb-4">Catégories</h3>
      <div className="space-y-2">
        <button
          onClick={() => handleCategoryChange('all')}
          className={`w-full text-left py-2 px-3 rounded-md transition-colors ${
            !selectedCategory ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'
          }`}
          data-testid="filter-all"
        >
          Toutes les catégories
        </button>
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => handleCategoryChange(category.id)}
            className={`w-full text-left py-2 px-3 rounded-md transition-colors ${
              selectedCategory === category.id ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'
            }`}
            data-testid={`filter-${category.slug}`}
          >
            {category.name}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <PublicLayout>
      <SEO
        title="Commander en ligne — Tapis, Décoration & Accessoires"
        description="Parcourez notre catalogue en ligne : tapis berbères, miroirs, vannerie, mobilier et accessoires de décoration. Livraison depuis Tours (37)."
        canonical="/commander-en-ligne"
      />
      {/* Header */}
      <section className="py-16 px-6 lg:px-12 bg-muted/30 border-b border-border">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-heading text-4xl md:text-5xl font-semibold mb-4">
            Commander en ligne
          </h1>
          <p className="text-muted-foreground text-lg mb-6">
            Découvrez notre sélection de tapis et accessoires de décoration
          </p>
          {/* Search bar */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un produit…"
              className="pl-9 pr-9 bg-white"
              data-testid="catalogue-search-input"
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                data-testid="clear-search-btn"
              >
                <X className="w-4 h-4 text-muted-foreground hover:text-foreground" />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Main content */}
      <section className="py-12 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex gap-12">
            {/* Desktop Sidebar */}
            <aside className="hidden lg:block w-64 flex-shrink-0">
              <FilterSidebar />
            </aside>

            {/* Products Grid */}
            <div className="flex-1">
              {/* Toolbar */}
              <div className="flex items-center justify-between mb-8 pb-4 border-b border-border">
                <div className="flex items-center gap-4">
                  {/* Mobile filter trigger */}
                  <Sheet>
                    <SheetTrigger asChild className="lg:hidden">
                      <Button variant="outline" size="sm" data-testid="mobile-filter-btn">
                        <SlidersHorizontal className="w-4 h-4 mr-2" />
                        Filtres
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="left">
                      <SheetHeader>
                        <SheetTitle>Filtres</SheetTitle>
                      </SheetHeader>
                      <div className="mt-6">
                        <FilterSidebar mobile />
                      </div>
                    </SheetContent>
                  </Sheet>

                  <span className="text-sm text-muted-foreground">
                    {products.length} produit{products.length > 1 ? 's' : ''}
                  </span>
                </div>

                <div className="flex items-center gap-4">
                  {/* Sort */}
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-40" data-testid="sort-select">
                      <SelectValue placeholder="Trier par" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="newest">Plus récents</SelectItem>
                      <SelectItem value="price-asc">Prix croissant</SelectItem>
                      <SelectItem value="price-desc">Prix décroissant</SelectItem>
                      <SelectItem value="name">Nom A-Z</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Grid toggle */}
                  <div className="hidden md:flex items-center gap-1 border border-border rounded-md p-1">
                    <button
                      onClick={() => setGridCols(2)}
                      className={`p-1.5 rounded ${gridCols === 2 ? 'bg-muted' : ''}`}
                      data-testid="grid-2"
                    >
                      <Grid2X2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setGridCols(3)}
                      className={`p-1.5 rounded ${gridCols === 3 ? 'bg-muted' : ''}`}
                      data-testid="grid-3"
                    >
                      <Grid3X3 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Products */}
              {loading ? (
                <div className={`grid gap-8 ${
                  gridCols === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                }`}>
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="animate-pulse">
                      <div className="aspect-[3/4] bg-muted rounded-md mb-4" />
                      <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                      <div className="h-4 bg-muted rounded w-1/2" />
                    </div>
                  ))}
                </div>
              ) : products.length > 0 ? (
              <>
                <div className={`grid gap-8 ${
                  gridCols === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                }`}>
                  {products.slice(0, visibleCount).map((product, index) => (
                    <motion.div
                      key={product.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: Math.min((index % PAGE_SIZE) * 0.03, 0.3) }}
                    >
                      <Link 
                        to={`/produit/${product.slug}`} 
                        className="group product-card block"
                        data-testid={`product-${product.slug}`}
                      >
                        <div className="aspect-[3/4] overflow-hidden rounded-md mb-4 bg-muted relative">
                          {/* Spinner tant que l'image n'est pas encore chargée ni tombée en erreur.
                              Clé sur product.id (pas index) — sinon au tri les cartes restaient vides. */}
                          {!imagesLoaded[product.id] && !imagesError[product.id] && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                            </div>
                          )}
                          {imagesError[product.id] && (
                            <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">
                              Image indisponible
                            </div>
                          )}
                          <img
                            src={optimizeImg(getImageUrl(product.images?.[0], 600) || 'https://images.unsplash.com/photo-1726208224138-80303abb73d3?crop=entropy&cs=srgb&fm=jpg&q=85')}
                            alt={product.name}
                            loading={index < 6 ? "eager" : "lazy"}
                            decoding="async"
                            fetchPriority={index < 6 ? "high" : "auto"}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            onLoad={() => setImagesLoaded(prev => ({ ...prev, [product.id]: true }))}
                            onError={() => setImagesError(prev => ({ ...prev, [product.id]: true }))}
                          />
                        </div>
                        <h3 className="font-heading text-lg font-medium mb-1 group-hover:text-primary transition-colors duration-300">
                          {product.name}
                        </h3>
                        <p className="text-muted-foreground text-sm mb-2 line-clamp-1">
                          {product.description}
                        </p>
                        <p className="text-primary font-semibold text-lg">
                          {typeof product.price === 'number' ? product.price.toFixed(2) : '—'} €
                        </p>
                      </Link>
                    </motion.div>
                  ))}
                </div>

                {/* Bouton Voir plus */}
                {visibleCount < products.length && (
                  <div className="flex flex-col items-center mt-12 gap-2">
                    <p className="text-sm text-muted-foreground">
                      {Math.min(visibleCount, products.length)} sur {products.length} produits
                    </p>
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={() => setVisibleCount(v => v + PAGE_SIZE)}
                      className="min-w-[180px]"
                      data-testid="load-more-btn"
                    >
                      Voir plus
                    </Button>
                  </div>
                )}
              </>
              ) : (
                <div className="text-center py-20">
                  <p className="text-muted-foreground text-lg mb-4">
                    Aucun produit trouvé dans cette catégorie.
                  </p>
                  <Button onClick={() => handleCategoryChange('all')} variant="outline">
                    Voir tous les produits
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
